import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangleIcon, ArrowLeftIcon, CheckCircle2Icon, ClockIcon, ExternalLinkIcon, MailIcon, PenLineIcon, PhoneIcon } from 'lucide-react';
import { DetailCard, FieldList } from '../components/detail/DetailCard';
import { ActivityTimeline } from '../components/detail/ActivityTimeline';
import { DocumentRequestPanel } from '../components/leads/DocumentRequestPanel';
import { LogCommunicationModal } from '../components/communication/LogCommunicationModal';
import { useToast } from '../contexts/ToastContext';
import { leads } from '../data/leads';
import { leadScenarios, leadTasks } from '../data/leadDetails';
import { preApprovals } from '../data/preApprovals';
import { activityLog } from '../data/activityHistory';
import type { CommunicationSubject, Lead } from '../types/portal';
const statusBadge: Record<Lead['status'], string> = {
  new: 'bg-brand-50 text-brand-700 ring-brand-200',
  working: 'bg-teal-50 text-teal-700 ring-teal-200',
  nurture: 'bg-navy-50 text-navy-600 ring-navy-200'
};
export function LeadDetailPage() {
  const {
    leadId
  } = useParams<{
    leadId: string;
  }>();
  const {
    showToast
  } = useToast();
  const [logging, setLogging] = useState(false);
  const lead = leads.find(item => item.id === leadId);
  if (!lead) {
    return <main className="w-full px-4 py-10 sm:px-6 lg:px-8" data-id="element-636" data-mp="3dm8uc5x1cpyk">
        <div className="mx-auto max-w-lg rounded-2xl border border-navy-100 bg-white p-8 text-center shadow-card" data-id="element-637" data-mp="2gxjdmix5a3e6">
          <AlertTriangleIcon className="mx-auto h-6 w-6 text-warn-600" aria-hidden="true" data-id="element-638" data-mp="2kz4lfputnrhi" />
          <h1 className="mt-3 text-lg font-semibold text-navy-900" data-id="element-639" data-mp="2pw4sa2zhwy9o">
            Lead not found
          </h1>
          <p className="mt-1 text-sm text-navy-500" data-id="element-640" data-mp="2uug4ulh6dmvl">
            This lead may have been reassigned or converted to a loan.
          </p>
          <Link to="/leads" className="mt-5 inline-flex h-11 items-center justify-center rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white hover:bg-navy-900" data-id="element-641" data-mp="a5s9n1bjzvw8">
            Back to My Leads
          </Link>
        </div>
      </main>;
  }
  const scenario = leadScenarios[lead.id];
  const tasks = leadTasks[lead.id] ?? [];
  const entries = activityLog[lead.id] ?? [];
  const preApproval = preApprovals.find(item => item.borrower === lead.name);
  const subject: CommunicationSubject = {
    id: lead.id,
    name: lead.name,
    context: `${lead.purpose} · ${lead.amount} · ${lead.program}`,
    phone: lead.phone,
    email: lead.email,
    goalCredit: 'your consumer conversations goal'
  };
  return <main className="w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8" data-id="element-642" data-mp="37ooyonc034aj">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4" data-id="element-643" data-mp="32vdqahrjifgi">
        <Link to="/leads" className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-navy-600 hover:text-navy-900" data-id="element-644" data-mp="2f61kzszc2rjh">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-645" data-mp="2a5nj6sobsdtw" />
          My Leads
        </Link>

        <header className="rounded-2xl border border-navy-100 bg-white p-5 shadow-card" data-id="element-646" data-mp="pnmfhtq91cjn">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between" data-id="element-647" data-mp="ui8vka79zta2">
            <div className="min-w-0" data-id="element-648" data-mp="fp6p5sz1q7b7">
              <div className="flex flex-wrap items-center gap-2" data-id="element-649" data-mp="kptc18gbywn2">
                <h1 className="text-2xl font-bold tracking-tight text-navy-900" data-id="element-650" data-mp="ko9rv6rk01g0">
                  {lead.name}
                </h1>
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ring-1 ${statusBadge[lead.status]}`} data-id="element-651" data-mp="1dfjtnrh68gnx">
                  {lead.status}
                </span>
                {lead.lastContactLabel === null ? <span className="inline-flex items-center gap-1 rounded-full bg-danger-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-danger-700 ring-1 ring-danger-200" data-id="element-652" data-mp="3k582kamr64ua">
                    <ClockIcon className="h-3 w-3" aria-hidden="true" data-id="element-653" data-mp="893c77y7psy8" />
                    Never contacted
                  </span> : null}
              </div>
              <p className="mt-2 text-sm text-navy-700" data-id="element-654" data-mp="3uo4wyu3kd2qc">
                {lead.purpose} · {lead.amount} · {lead.program} · {lead.market}
              </p>
              <p className="mt-1 text-xs text-navy-500" data-id="element-655" data-mp="3pqwaihbducja">
                {lead.id} · {lead.source} · {lead.assignedLabel}
              </p>
            </div>

            <div className="flex shrink-0 flex-col gap-2 sm:flex-row" data-id="element-656" data-mp="m835wu0l0y19">
              <button type="button" onClick={() => setLogging(true)} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700" data-id="element-657" data-mp="h9i1sji7lu7j">
                <PenLineIcon className="h-4 w-4" aria-hidden="true" data-id="element-658" data-mp="3211z846pow15" />
                Log communication
              </button>
              <button type="button" onClick={() => showToast(`Opening ${lead.id} — ${lead.name} in Arive.`)} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-659" data-mp="1dg3big3jcl8a">
                Open in Arive
                <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-660" data-mp="1w6kzk7dfasnb" />
              </button>
              {preApproval ? <Link to="/pre-approvals" className="inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-661" data-mp="214w9sj7mr562">
                  View pre-approval
                </Link> : null}
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-id="element-662" data-mp="fscl702t3pmr">
          <div className="flex flex-col gap-4 lg:col-span-2" data-id="element-663" data-mp="fsl0eymej714">
            <DetailCard title="Loan scenario" data-id="element-664" data-mp="1j2gt888vdk54">
              {scenario ? <>
                  <FieldList items={[{
                label: 'Purpose',
                value: lead.purpose
              }, {
                label: 'Loan program',
                value: lead.program
              }, {
                label: 'Requested amount',
                value: lead.amount
              }, {
                label: 'Down payment',
                value: scenario.downPayment
              }, {
                label: 'Estimated payment',
                value: scenario.estimatedPayment
              }, {
                label: 'Credit range',
                value: scenario.creditRange
              }, {
                label: 'Property type',
                value: scenario.propertyType
              }, {
                label: 'Co-borrower',
                value: scenario.coBorrower
              }, {
                label: 'Buying timeline',
                value: scenario.timeline
              }, {
                label: 'Market',
                value: lead.market
              }]} data-id="element-665" data-mp="1e4x9wtxbeh6b" />
                  <p className="mt-5 rounded-xl bg-navy-50 p-4 text-sm text-navy-700" data-id="element-666" data-mp="2zfuah0ba54po">
                    {scenario.notes}
                  </p>
                </> : <p className="text-sm text-navy-500" data-id="element-667" data-mp="2pg3gr0ujmtqn">
                  No scenario details captured yet. Log your first conversation
                  to fill this in.
                </p>}
            </DetailCard>

            <DocumentRequestPanel leadName={lead.name} onOpen={() => showToast(`Opening the document request for ${lead.name}.`)} data-id="element-668" data-mp="2gnibmnju4lka" />

            <DetailCard title="Activity history" data-id="element-669" data-mp="26oiz2tvy85s0">
              <ActivityTimeline entries={entries} data-id="element-670" data-mp="2box9ujoxpttq" />
            </DetailCard>
          </div>

          <div className="flex flex-col gap-4" data-id="element-671" data-mp="1xpfh5t8ipwf3">
            <DetailCard title="Contact" data-id="element-672" data-mp="1xqqt4gc4aaha">
              <ul className="space-y-3 text-sm" data-id="element-673" data-mp="21raobed4r40s">
                <li className="flex items-center gap-2 text-navy-800" data-id="element-674" data-mp="21r0wdl8iymsg">
                  <PhoneIcon className="h-4 w-4 shrink-0 text-navy-400" aria-hidden="true" data-id="element-675" data-mp="2i60e3ot6f63v" />
                  <a href={`tel:${lead.phone.replace(/[^0-9]/g, '')}`} className="font-semibold hover:text-brand-700" data-id="element-676" data-mp="2d64bnu04nmqf">
                    {lead.phone}
                  </a>
                </li>
                <li className="flex items-center gap-2 text-navy-800" data-id="element-677" data-mp="1z98wdjta8z1x">
                  <MailIcon className="h-4 w-4 shrink-0 text-navy-400" aria-hidden="true" data-id="element-678" data-mp="1ugxnvskh84xr" />
                  <a href={`mailto:${lead.email}`} className="break-all font-semibold hover:text-brand-700" data-id="element-679" data-mp="1pinsbguub2bm">
                    {lead.email}
                  </a>
                </li>
              </ul>
              <div className="mt-4 space-y-1 border-t border-navy-100 pt-4 text-xs text-navy-500" data-id="element-680" data-mp="1bid12cpmblpx">
                <p data-id="element-681" data-mp="3n6q5pfbnry80">Source: {lead.source}</p>
                <p data-id="element-682" data-mp="3n81b1jkr5e18">{lead.assignedLabel}</p>
                <p data-id="element-683" data-mp="3r70ndp8tcd2p">
                  Last contact:{' '}
                  {lead.lastContactLabel ?? 'No communication logged'}
                </p>
              </div>
              <p className="mt-4 rounded-lg bg-navy-50 px-3 py-2 text-xs text-navy-600" data-id="element-684" data-mp="3w4tuckhxxb41">
                Outbound calls and texts from your own phone aren’t captured
                automatically — log them here.
              </p>
            </DetailCard>

            <DetailCard title="Open tasks" data-id="element-685" data-mp="38i3qddm176gs">
              {tasks.length === 0 ? <p className="flex items-center gap-2 text-sm text-navy-500" data-id="element-686" data-mp="3dfvio8ql8uli">
                  <CheckCircle2Icon className="h-4 w-4 text-success-600" aria-hidden="true" data-id="element-687" data-mp="3i6lr5uub9dkf" />
                  No open tasks for this lead.
                </p> : <ul className="space-y-3" data-id="element-688" data-mp="2pp9n3gxrt2se">
                  {tasks.map(task => <li key={task.id} className={`rounded-xl border p-3 ${task.urgent ? 'border-danger-200 bg-danger-50' : 'border-navy-100'}`} data-id="element-689" data-mp="1rwev4aikm03a">
                      <p className={`text-sm font-semibold ${task.urgent ? 'text-danger-700' : 'text-navy-900'}`} data-id="element-690" data-mp="26tebonveh39w">
                        {task.label}
                      </p>
                      <p className="mt-1 text-xs text-navy-500" data-id="element-691" data-mp="1j4v121ovoeen">
                        {task.id} · {task.due}
                      </p>
                      <button type="button" onClick={() => showToast(`Task ${task.id} marked complete.`)} className="mt-3 inline-flex h-10 items-center justify-center rounded-lg border border-navy-200 bg-white px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-692" data-mp="1nx61r6rg0489">
                        Mark complete
                      </button>
                    </li>)}
                </ul>}
            </DetailCard>
          </div>
        </div>
      </div>

      <LogCommunicationModal subject={logging ? subject : null} onClose={() => setLogging(false)} onLogged={showToast} data-id="element-693" data-mp="2pqxyd6p0ryzd" />
    </main>;
}